'use client';

import { APP_SCORING_FORMAT } from '@/constants';
import { TAppScoringFormat } from '@/types';

// ================================
// Types
// ================================

export interface ISelectScoreValueOptions {
  /** Raw numerical score value. */
  score?: number | null;

  /** Ratio-style percentage value (e.g. 0.58 for 58%). */
  scorePercentage?: number | null;

  /**
   * Value returned when the preferred score source is missing.
   * Defaults to 0 to preserve existing UI fallback behavior.
   */
  fallback?: number;

  /** Optional format override for deterministic tests and custom callers. */
  scoringFormat?: TAppScoringFormat;
}

// ================================
// Public Domain Policy
// ================================

/**
 * Selects the score value that should be shown in score displays.
 *
 * Policy:
 * - `percentage` format uses `scorePercentage`.
 * - All numerical formats use `score`.
 * - Missing values return `fallback` (default 0).
 */
export const selectDisplayScoreValue = ({
  fallback = 0,
  score,
  scorePercentage,
  scoringFormat = APP_SCORING_FORMAT,
}: ISelectScoreValueOptions): number => {
  switch (scoringFormat) {
    case 'percentage':
      return scorePercentage ?? fallback;
    default:
      return score ?? fallback;
  }
};

/**
 * Selects the score value to feed into skill-level derivation logic.
 *
 * This currently mirrors display selection and is intentionally separated to
 * allow future divergence without updating every UI call-site.
 */
export const selectSkillLevelInputValue = (options: ISelectScoreValueOptions): number => {
  return selectDisplayScoreValue(options);
};
